// Bookmark List Component

import api from '../api.js';

let bookmarks = [];
let categories = [];
let tags = [];
let currentFilter = {
  search: '',
  categoryId: null,
  tagIds: [],
  sortBy: 'created_at',
  sortOrder: 'desc',
};

export async function loadBookmarks() {
  try {
    const params = {
      sort_by: currentFilter.sortBy,
      sort_order: currentFilter.sortOrder,
    };

    if (currentFilter.categoryId) {
      params.category_id = currentFilter.categoryId;
    }

    if (currentFilter.tagIds.length > 0) {
      params.tag_ids = currentFilter.tagIds.join(',');
    }

    let response;
    if (currentFilter.search) {
      response = await api.searchBookmarks(currentFilter.search, params);
    } else {
      response = await api.getBookmarks(params);
    }

    // Handle paginated response from backend
    bookmarks = response.items || response || [];

    renderBookmarkList();
  } catch (error) {
    console.error('Failed to load bookmarks:', error);
    showError('Fehler beim Laden der Lesezeichen: ' + error.message);
  }
}

export async function loadCategories() {
  try {
    const response = await api.getCategories();
    // Handle paginated response from backend
    categories = response.items || response || [];
    renderCategoryFilter();
  } catch (error) {
    console.error('Failed to load categories:', error);
  }
}

export async function loadTags() {
  try {
    const response = await api.getTags();
    // Handle paginated response from backend
    tags = response.items || response || [];
    renderTagFilter();
  } catch (error) {
    console.error('Failed to load tags:', error);
  }
}

export function renderBookmarkList() {
  const container = document.getElementById('bookmark-list');
  if (!container) return;

  if (!bookmarks || bookmarks.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <p>Keine Lesezeichen gefunden.</p>
        <button id="add-first-bookmark" class="btn btn-primary">Erstes Lesezeichen hinzufügen</button>
      </div>
    `;
    document.getElementById('add-first-bookmark')?.addEventListener('click', () => {
      showBookmarkForm();
    });
    return;
  }

  const bookmarkHTML = bookmarks.map((bookmark) => `
    <div class="bookmark-item" data-id="${bookmark.id}">
      <div class="bookmark-header">
        ${bookmark.favicon ? `<img src="${bookmark.favicon}" alt="" class="bookmark-favicon" onerror="this.style.display='none'">` : ''}
        <h3><a href="${bookmark.url}" target="_blank" rel="noopener noreferrer">${escapeHtml(bookmark.title || bookmark.url)}</a></h3>
      </div>

      ${bookmark.description ? `<p class="bookmark-description">${escapeHtml(bookmark.description)}</p>` : ''}

      <div class="bookmark-meta">
        <span class="bookmark-url">${escapeHtml(bookmark.url)}</span>
        ${bookmark.category ? `<span class="category-badge" style="background-color: ${bookmark.category.color || '#999'}">${escapeHtml(bookmark.category.name)}</span>` : ''}
        ${bookmark.tags && bookmark.tags.length > 0 ? bookmark.tags.map(tag => `<span class="tag-badge">${escapeHtml(tag.name)}</span>`).join('') : ''}
      </div>

      <div class="bookmark-actions">
        <button class="btn-icon edit-bookmark" data-id="${bookmark.id}" title="Bearbeiten">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
        <button class="btn-icon delete-bookmark" data-id="${bookmark.id}" title="Löschen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
  `).join('');

  container.innerHTML = bookmarkHTML;

  // Attach event listeners
  document.querySelectorAll('.edit-bookmark').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const id = parseInt(e.currentTarget.dataset.id);
      const bookmark = bookmarks.find((b) => b.id === id);
      if (bookmark) {
        showBookmarkForm(bookmark);
      }
    });
  });

  document.querySelectorAll('.delete-bookmark').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const id = parseInt(e.currentTarget.dataset.id);
      if (confirm('Möchten Sie dieses Lesezeichen wirklich löschen?')) {
        await deleteBookmark(id);
      }
    });
  });
}

export function renderCategoryFilter() {
  const container = document.getElementById('category-filter');
  if (!container) return;

  const categoryOptions = categories.map(cat =>
    `<option value="${cat.id}" ${currentFilter.categoryId === cat.id ? 'selected' : ''}>${escapeHtml(cat.name)}</option>`
  ).join('');

  container.innerHTML = `
    <option value="">Alle Kategorien</option>
    ${categoryOptions}
  `;
}

export function renderTagFilter() {
  const container = document.getElementById('tag-filter');
  if (!container) return;

  const tagCheckboxes = tags.map(tag => `
    <label class="tag-filter-item">
      <input type="checkbox" value="${tag.id}" ${currentFilter.tagIds.includes(tag.id) ? 'checked' : ''}>
      ${escapeHtml(tag.name)}
    </label>
  `).join('');

  container.innerHTML = tagCheckboxes;
}

export async function deleteBookmark(id) {
  try {
    await api.deleteBookmark(id);
    bookmarks = bookmarks.filter(b => b.id !== id);
    renderBookmarkList();
    showSuccess('Lesezeichen gelöscht');
  } catch (error) {
    console.error('Failed to delete bookmark:', error);
    showError('Fehler beim Löschen: ' + error.message);
  }
}

export function showBookmarkForm(bookmark = null) {
  const event = new CustomEvent('show-bookmark-form', { detail: bookmark });
  document.dispatchEvent(event);
}

export function updateFilter(updates) {
  currentFilter = { ...currentFilter, ...updates };
  loadBookmarks();
}

export function getBookmarks() {
  return bookmarks;
}

export function getCategories() {
  return categories;
}

export function getTags() {
  return tags;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function showError(message) {
  const event = new CustomEvent('show-notification', {
    detail: { type: 'error', message }
  });
  document.dispatchEvent(event);
}

function showSuccess(message) {
  const event = new CustomEvent('show-notification', {
    detail: { type: 'success', message }
  });
  document.dispatchEvent(event);
}
